/**
 * The platform-wide top bar — brand + user menu — shared by every area (chat,
 * admin, future features). Renders the design system's AppShell.Header, so
 * consumers compose it as the first child of <DSAppShell>.
 */
import { useMsal } from '@azure/msal-react';
import { rememberSignedOut } from '../features/auth/signInMemory.js';
import { Link } from 'react-router-dom';
import {
  AppShell as DSAppShell,
  SCLogo,
  SideNav,
  UserMenu,
} from '@servicecompressionllc/sc-design-system';
import { useMe } from '../hooks/useMe.js';
import { hasAdminAccess } from '../lib/permissions.js';

interface PlatformHeaderProps {
  /**
   * When set, renders the SideNav drawer trigger (a hamburger) at the far left
   * — shown only below `lg`. The hosting area wires it to its drawer's open
   * state. Omit it for areas without a collapsible sidebar.
   */
  onMenuClick?: () => void;
}

export const PlatformHeader = ({ onMenuClick }: PlatformHeaderProps) => {
  const me = useMe();
  const { instance } = useMsal();

  return (
    <DSAppShell.Header>
      {onMenuClick ? <SideNav.Trigger onClick={onMenuClick} /> : null}
      <DSAppShell.Brand>
        <SCLogo className="h-[26px] w-auto" />
        <div className="hidden h-6 w-px bg-white/10 md:block" />
        <span className="hidden md:inline">SC Platform</span>
      </DSAppShell.Brand>
      <DSAppShell.Spacer />
      {me.data ? (
        <UserMenu>
          <UserMenu.Trigger>
            <UserMenu.Avatar email={me.data.user.email} />
            <span className="hidden text-left sm:block">
              <span className="block text-xs font-medium leading-tight text-white">
                {me.data.user.name || me.data.user.email}
              </span>
              <span className="block text-[10px] leading-tight text-white/50">
                {me.data.user.email}
              </span>
            </span>
          </UserMenu.Trigger>
          <UserMenu.Content>
            <UserMenu.Label>
              <div className="truncate font-medium text-ink">
                {me.data.user.name || me.data.user.email}
              </div>
              <div className="truncate text-xs text-ink-subtle">{me.data.user.email}</div>
              <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                <span className="rounded bg-surface-hover px-1.5 py-0.5 font-medium text-ink-muted">
                  {me.data.tenant.name}
                </span>
              </div>
            </UserMenu.Label>
            {/* One door for every admin capability: /admin lands each caller
                on the first surface they hold a permission for, and the nav
                inside shows exactly their items. */}
            {hasAdminAccess(me.data.user) && (
              <UserMenu.Item asChild>
                <Link to="/admin">Admin</Link>
              </UserMenu.Item>
            )}
            <UserMenu.Item asChild>
              <Link to="/settings">Settings</Link>
            </UserMenu.Item>
            <UserMenu.Item
              onSelect={() => {
                rememberSignedOut();
                void instance.logoutRedirect();
              }}
            >
              Sign out
            </UserMenu.Item>
          </UserMenu.Content>
        </UserMenu>
      ) : (
        <span className="text-xs text-white/60">{me.isLoading ? 'loading…' : 'not signed in'}</span>
      )}
    </DSAppShell.Header>
  );
};
